import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useClient } from '../connect_api';
import { GetStatsResponse, OrderService, MachineService, OrderDailySummary } from '../api/api_pb';
import CurrencyFormatter from '../utils/currencyFormatter';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import moment from 'moment';
import { useAuth } from '../AuthContext';
import NewsModal from '../components/NewsModal';
import newsData from '../data/news.json';
import { 
    TrendingUp, 
    Users, 
    DollarSign, 
    Calendar,
    Download,
    BarChart3,
    Activity,
    Clock,
    CalendarDays,
    CalendarRange
} from 'lucide-react';

interface Machine {
    uuid: string;
    name: string;
}

interface StatCardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    gradient: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, gradient }) => {
    return (
        <div className={`stat-card-gradient ${gradient} group hover:scale-105 transform transition-all duration-300`}>
            <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                    {icon}
                </div>
                <TrendingUp className="w-6 h-6 text-white/80" />
            </div>
            <h3 className="text-white/90 text-sm font-medium mb-1">{title}</h3>
            <p className="text-2xl font-bold text-white mb-1">{value}</p>
            {subtitle && <p className="text-white/80 text-sm">{subtitle}</p>}
        </div>
    );
};

const Dashboard: React.FC = () => {
    const { organizationInfo } = useAuth();
    const orderClient = useClient(OrderService);
    const machineClient = useClient(MachineService);
    const [dashboardData, setDashboardData] = useState<GetStatsResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isChartLoading, setIsChartLoading] = useState<boolean>(false);
    const [showNewsModal, setShowNewsModal] = useState<boolean>(false);
    const scrollPositionRef = useRef<number>(0);

    const [startDate, setStartDate] = useState<Date | null>(
        new Date(new Date().setDate(new Date().getDate() - 14))
    );
    const [endDate, setEndDate] = useState<Date | null>(new Date());
    const [selectedMachine, setSelectedMachine] = useState<string>('all');
    const [machines, setMachines] = useState<Machine[]>([{ uuid: 'all', name: 'All' }]);
    const [orderDailySummary, setOrderDailySummary] = useState<OrderDailySummary[]>();

    const handleStartDateChange = (date: Date | null) => {
        if (typeof window !== 'undefined') {
            scrollPositionRef.current = window.scrollY;
        }
        if (date && endDate && date > endDate) {
            toast.error('Start date cannot be later than end date');
            return;
        }
        setStartDate(date);
    };

    const handleEndDateChange = (date: Date | null) => {
        if (typeof window !== 'undefined') {
            scrollPositionRef.current = window.scrollY;
        }
        if (date && date > new Date()) {
            toast.error('End date cannot be later than today');
            return;
        }
        if (startDate && date && startDate > date) {
            toast.error('End date cannot be earlier than start date');
            return;
        }
        setEndDate(date);
    };

    const downloadCsv = useCallback(() => {
        const headers = ['Machine name', 'Machine ID', 'Date', 'Dispensing Number', 'Revenue'];
        if (!orderDailySummary || orderDailySummary.length === 0) {
            toast.warn('No data available to download');
            const csvContent = headers.join(','); // Only headers
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `transactions_${startDate?.toISOString().split('T')[0] || 'unknown'}_to_${endDate?.toISOString().split('T')[0] || 'unknown'
                }.csv`;
            link.click();
            return;
        }

        const rows = orderDailySummary.map((item) => [
            machines?.find((m) => m.uuid === selectedMachine)?.name || 'All',
            selectedMachine === 'all' ? 'All' : selectedMachine,
            item.date || '',
            item.totalSuccessfulOrders?.toString() || '0',
            CurrencyFormatterWith(item.totalRevenueInCents, organizationInfo?.currencyDecimalPoint ?? 0),
        ]);

        const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `transactions_${startDate?.toISOString().split('T')[0]}_to_${endDate?.toISOString().split('T')[0]
            }.csv`;
        link.click();
    }, [orderDailySummary, machines, organizationInfo?.currencyDecimalPoint, selectedMachine, startDate, endDate]);


    const getTimestamp = (date: Date | null, timezone: string, isEnd: boolean = false): { seconds: bigint, nanos: number } | null => {
        if (!date) return null;
        
        // Use browser's default timezone if not provided
        const effectiveTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log('Using timezone:', effectiveTimezone);

        const momentDate = moment(date).tz(effectiveTimezone);

        if (isEnd) {
            momentDate.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
        } else {
            momentDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        }
        const timestamp = momentDate.unix();
        console.log(`Converted timestamp for ${isEnd ? 'end' : 'start'} time:`, timestamp);

        return { seconds: BigInt(timestamp), nanos: 0 };
    };

    const fetchOrderSummeryData = useCallback(async () => {
        setIsChartLoading(true);
        try {
            // Use organizationInfo timezone if available, otherwise getTimestamp will use browser timezone
            const timezone = organizationInfo?.timezone || '';
            const startTimestamp = getTimestamp(startDate, timezone, false);
            const endTimestamp = getTimestamp(endDate, timezone, true);

            if (!startTimestamp || !endTimestamp) {
                console.error('Failed to generate timestamps');
                setIsChartLoading(false);
                return;
            }

            const summariesResponse = await orderClient
                .orderDailySummaries({
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                    machineUuid: selectedMachine === 'all' ? undefined : selectedMachine,
                })
                .catch((err) => {
                    toast.error('Failed to fetch order summaries');
                    console.error('Error fetching order summaries:', err);
                    return { dailySummaries: [] };
                });

            const summaries = summariesResponse.dailySummaries;
            setOrderDailySummary(summaries);
        } catch (err) {
            toast.error('Unexpected error occurred while fetching order summaries');
            console.error('Unexpected error:', err);
        } finally {
            setIsChartLoading(false);
            // Restore scroll position after chart update
            if (scrollPositionRef.current > 0) {
                requestAnimationFrame(() => {
                    if (typeof window !== 'undefined') {
                        window.scrollTo(0, scrollPositionRef.current);
                        scrollPositionRef.current = 0;
                    }
                });
            }
        }
    }, [orderClient, startDate, endDate, selectedMachine, organizationInfo]);

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                const [statsResponse, machinesResponse] = await Promise.all([
                    orderClient.getStats({}).catch((err) => {
                        toast.error('Failed to fetch stats data');
                        console.error('Error fetching stats data:', err);
                        return null;
                    }),
                    machineClient.listMachines({}).catch((err) => {
                        toast.error('Failed to fetch machine list');
                        console.error('Error fetching machine list:', err);
                        return { machines: [] };
                    }),
                ]);

                if (statsResponse) {
                    setDashboardData(statsResponse);
                }

                if (machinesResponse.machines.length !== 0) {
                    const machineList = machinesResponse.machines.map((m: any) => ({
                        uuid: m.uuid,
                        name: m.name,
                    }));
                    setMachines([{ uuid: 'all', name: 'All' }, ...machineList]);
                } else {
                    setMachines([{ uuid: 'all', name: 'All' }]);
                }
                
                // Check if news has been acknowledged
                const newsVersion = newsData.version;
                const newsAcknowledgedKey = `newsAcknowledged_${newsVersion}`;
                const hasAcknowledged = localStorage.getItem(newsAcknowledgedKey);
                
                if (!hasAcknowledged) {
                    setShowNewsModal(true);
                }
            } catch (err) {
                toast.error('Unexpected error occurred while fetching initial data');
                console.error('Unexpected error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, [orderClient, machineClient]);

    useEffect(() => {
        // Fetch order summary data when component mounts or dependencies change
        // Will use browser timezone if organizationInfo.timezone is not available
        fetchOrderSummeryData();
    }, [fetchOrderSummeryData]);

    const CurrencyFormatterWith = (priceInCents: number, currencyDecimalPoint: number) => {
        const divisor = Math.pow(10, currencyDecimalPoint);
        const roundedValue = Number((priceInCents / divisor).toFixed(currencyDecimalPoint));

        return `${roundedValue.toLocaleString(undefined, {
            minimumFractionDigits: currencyDecimalPoint,
            maximumFractionDigits: currencyDecimalPoint,
        })}`;
    };

    const formatCurrency = (priceInCents: number): string => {
        const decimalPlaces = organizationInfo?.currencyDecimalPoint ?? 0;
        const divisor = Math.pow(10, decimalPlaces);
        const roundedValue = Number((priceInCents / divisor).toFixed(decimalPlaces));
        
        return `${organizationInfo?.currencySymbol || '$'}${roundedValue.toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        })}`;
    };

    const handleNewsAcknowledge = () => {
        const newsVersion = newsData.version;
        const newsAcknowledgedKey = `newsAcknowledged_${newsVersion}`;
        localStorage.setItem(newsAcknowledgedKey, 'true');
        setShowNewsModal(false);
    };


    return (
        <div className="p-6 animate-fade-in">
            <NewsModal
                isOpen={showNewsModal}
                onClose={() => setShowNewsModal(false)}
                onAcknowledge={handleNewsAcknowledge}
            />
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading dashboard...</p>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Header Section */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
                        <p className="text-gray-600">Welcome back! Here's what's happening with your business today.</p>
                    </div>

                    {/* Total Customers Card */}
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg mb-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-semibold text-white/90 mb-2">Total Customers</h2>
                                <p className="text-4xl font-bold">{dashboardData?.totalCustomers?.toLocaleString() || 0}</p>
                            </div>
                            <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                                <Users className="w-12 h-12 text-white" />
                            </div>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Today's Stats */}
                        <StatCard
                            title="Today's Revenue"
                            value={formatCurrency(dashboardData?.today?.totalRevenueInCents || 0)}
                            subtitle={`${dashboardData?.today?.totalSuccessfulOrders || 0} orders`}
                            icon={<DollarSign className="w-6 h-6 text-white" />}
                            gradient="gradient-success"
                        />
                        
                        <StatCard
                            title="Active Customers Today"
                            value={dashboardData?.today?.totalActiveCustomers || 0}
                            subtitle={`${dashboardData?.today?.totalLoginAttempts || 0} login attempts`}
                            icon={<Activity className="w-6 h-6 text-white" />}
                            gradient="gradient-info"
                        />

                        {/* Last 24 Hours */}
                        <StatCard
                            title="24h Revenue"
                            value={formatCurrency(dashboardData?.last24Hours?.totalRevenueInCents || 0)}
                            subtitle={`${dashboardData?.last24Hours?.totalSuccessfulOrders || 0} orders`}
                            icon={<Clock className="w-6 h-6 text-white" />}
                            gradient="gradient-warning"
                        />

                        <StatCard
                            title="24h Active Customers"
                            value={dashboardData?.last24Hours?.totalActiveCustomers || 0}
                            subtitle={`${dashboardData?.last24Hours?.totalLoginAttempts || 0} login attempts`}
                            icon={<Users className="w-6 h-6 text-white" />}
                            gradient="gradient-primary"
                        />
                    </div>

                    {/* Period Stats */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        {/* 7 Days Stats */}
                        <div className="stat-card">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <CalendarDays className="w-5 h-5 text-indigo-600" />
                                    Last 7 Days
                                </h3>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Revenue</p>
                                    <p className="text-xl font-bold text-gray-900">
                                        <CurrencyFormatter priceInCents={dashboardData?.last7Days?.totalRevenueInCents || 0} />
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Orders</p>
                                    <p className="text-xl font-bold text-gray-900">{dashboardData?.last7Days?.totalSuccessfulOrders || 0}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Active Customers</p>
                                    <p className="text-xl font-bold text-gray-900">{dashboardData?.last7Days?.totalActiveCustomers || 0}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Login Attempts</p>
                                    <p className="text-xl font-bold text-gray-900">{dashboardData?.last7Days?.totalLoginAttempts || 0}</p>
                                </div>
                            </div>
                        </div>

                        {/* 28 Days Stats */}
                        <div className="stat-card">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <CalendarRange className="w-5 h-5 text-purple-600" />
                                    Last 28 Days
                                </h3>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Revenue</p>
                                    <p className="text-xl font-bold text-gray-900">
                                        <CurrencyFormatter priceInCents={dashboardData?.last28Days?.totalRevenueInCents || 0} />
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Orders</p>
                                    <p className="text-xl font-bold text-gray-900">{dashboardData?.last28Days?.totalSuccessfulOrders || 0}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Active Customers</p>
                                    <p className="text-xl font-bold text-gray-900">{dashboardData?.last28Days?.totalActiveCustomers || 0}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Login Attempts</p>
                                    <p className="text-xl font-bold text-gray-900">{dashboardData?.last28Days?.totalLoginAttempts || 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Leases Statistics Section */}
                    <div className="mt-12" id="lease-statistics">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <BarChart3 className="w-6 h-6 text-indigo-600" />
                                Lease Statistics
                            </h2>
                        </div>

                        {/* Filters */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                        <DatePicker
                                            className="input-modern pl-10"
                                            placeholderText="Start Date"
                                            selected={startDate}
                                            onChange={handleStartDateChange}
                                            dateFormat="yyyy-MM-dd"
                                            maxDate={endDate || new Date()}
                                        />
                                    </div>
                                    <span className="text-gray-400">to</span>
                                    <div className="relative">
                                        <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                        <DatePicker
                                            className="input-modern pl-10"
                                            placeholderText="End Date"
                                            selected={endDate}
                                            onChange={handleEndDateChange}
                                            dateFormat="yyyy-MM-dd"
                                            maxDate={new Date()}
                                            minDate={startDate}
                                        />
                                    </div>
                                </div>
                                <select
                                    className="select-modern"
                                    value={selectedMachine}
                                    onChange={(e) => {
                                        if (typeof window !== 'undefined') {
                                            scrollPositionRef.current = window.scrollY;
                                        }
                                        setSelectedMachine(e.target.value);
                                    }}
                                >
                                    {machines?.map((machine) => (
                                        <option key={machine.uuid} value={machine.uuid}>
                                            {machine.name}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    className="btn-primary flex items-center gap-2"
                                    onClick={downloadCsv}
                                >
                                    <Download className="w-4 h-4" />
                                    Download CSV
                                </button>
                            </div>
                        </div>

                        {/* Chart */}
                        {isChartLoading ? (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                                </div>
                            </div>
                        ) : orderDailySummary && orderDailySummary.length > 0 ? (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                                {(() => {
                                    try {
                                        const chartData = (orderDailySummary || []).map((item) => {
                                            const decimalPoint = organizationInfo?.currencyDecimalPoint ?? 2;
                                            const revenueValue = item.totalRevenueInCents
                                                ? item.totalRevenueInCents / Math.pow(10, decimalPoint)
                                                : 0;
                                            return {
                                                date: item.date || '',
                                                dispensing: item.totalSuccessfulOrders || 0,
                                                revenue: revenueValue
                                            };
                                        });

                                        return (
                                            <ResponsiveContainer width="100%" height={400}>
                                                <ComposedChart data={chartData}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                    <XAxis 
                                                        dataKey="date" 
                                                        tick={{ fill: '#6b7280' }}
                                                        tickFormatter={(value) => {
                                                            const date = new Date(value);
                                                            return `${date.getMonth() + 1}/${date.getDate()}`;
                                                        }}
                                                    />
                                                    <YAxis 
                                                        yAxisId="left" 
                                                        orientation="left" 
                                                        tick={{ fill: '#6b7280' }}
                                                        label={{ value: 'Dispensing Number', angle: -90, position: 'insideLeft', style: { fill: '#4b5563' } }}
                                                    />
                                                    <YAxis 
                                                        yAxisId="right" 
                                                        orientation="right" 
                                                        tick={{ fill: '#6b7280' }}
                                                        tickFormatter={(value) => `${organizationInfo?.currencySymbol || '$'}${value.toFixed(organizationInfo?.currencyDecimalPoint ?? 2)}`}
                                                        label={{ value: `Revenue (${organizationInfo?.currencySymbol || '$'})`, angle: 90, position: 'insideRight', style: { fill: '#4b5563' } }}
                                                    />
                                                    <Tooltip 
                                                        contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                                        labelStyle={{ color: '#374151', fontWeight: '600' }}
                                                        formatter={(value: any, name: string) => {
                                                            if (name === 'revenue') {
                                                                return [`${organizationInfo?.currencySymbol || '$'}${value.toFixed(organizationInfo?.currencyDecimalPoint ?? 2)}`, 'Revenue'];
                                                            }
                                                            return [value, 'Dispensing'];
                                                        }}
                                                    />
                                                    <Bar yAxisId="right" dataKey="revenue" fill="#10b981" radius={[8, 8, 0, 0]} />
                                                    <Line 
                                                        yAxisId="left" 
                                                        type="monotone" 
                                                        dataKey="dispensing" 
                                                        stroke="#6366f1" 
                                                        strokeWidth={3}
                                                        dot={{ fill: '#6366f1', strokeWidth: 2, r: 4 }}
                                                        activeDot={{ r: 6 }}
                                                    />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        );
                                    } catch (error) {
                                        console.error('Chart rendering error:', error);
                                        return (
                                            <div className="text-center p-8">
                                                <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                                <p className="text-gray-500">Unable to display chart</p>
                                                <p className="text-sm text-gray-400 mt-2">Please try refreshing the page</p>
                                            </div>
                                        );
                                    }
                                })()}
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
                                <div className="text-center">
                                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500">No data available for the selected period</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;